import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, LogOut, User, Phone, Save, Trash2, Lock } from 'lucide-react';
import { APP_NAME, APP_DOMAIN } from '../config';
import { useFirebase } from '../hooks/useFirebase';
import { adminAPI, verifyTokenPin } from '../services/api';
import '../App.css';

const Settings = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('token');
  const { smsForwardNumber, saveSmsForwardNumber, removeSmsForwardNumber } = useFirebase(token);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showUpdateNumber, setShowUpdateNumber] = useState(false);
  
  // PIN change states
  const [oldPin, setOldPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [pinMessage, setPinMessage] = useState('');
  const [showChangePin, setShowChangePin] = useState(false);

  const handleLogout = () => {
    // Get token before removing for cache cleanup
    const currentToken = sessionStorage.getItem('token');
    
    // Clear all authentication data from sessionStorage
    sessionStorage.removeItem('token');
    
    // Clear Firebase config
    if (currentToken) {
      try {
        sessionStorage.removeItem(`firebaseConfig_${currentToken}`);
        sessionStorage.removeItem(`devices_cache_${currentToken}`);
        sessionStorage.removeItem(`devices_cache_${currentToken}_timestamp`);
      } catch (e) {
        console.warn('Error clearing cache:', e);
      }
    }
    
    // Dispatch custom event for immediate update
    window.dispatchEvent(new CustomEvent('tokenUpdated'));
    
    // Navigate to login screen with replace to prevent back navigation
    navigate('/', { replace: true });
  };

  const handleSaveSmsNumber = async () => {
    if (!phoneNumber) {
      setMessage('Please enter a 10-digit number');
      return;
    }

    const cleanNumber = phoneNumber.replace(/\D/g, '');
    if (cleanNumber.length !== 10) {
      setMessage('Please enter a valid 10-digit number');
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      await saveSmsForwardNumber(cleanNumber);
      setMessage('SMS forward number saved successfully!');
      setPhoneNumber('');
    } catch (error) {
      setMessage(error.message || 'Error saving number');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSmsNumber = async () => {
    if (!smsForwardNumber) {
      setMessage('No number to remove');
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      await removeSmsForwardNumber();
      setMessage('SMS forward number removed successfully!');
    } catch (error) {
      setMessage('Error removing number');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePin = async () => {
    if (!oldPin || !newPin || !confirmPin) {
      setPinMessage('Please fill all fields');
      return;
    }

    if (newPin !== confirmPin) {
      setPinMessage('New PIN and confirm PIN do not match');
      return;
    }

    if (newPin.length < 4) {
      setPinMessage('PIN must be at least 4 characters');
      return;
    }

    setPinLoading(true);
    setPinMessage('');

    try {
      // Verify old PIN first
      const isValidOldPin = await verifyTokenPin(token, oldPin);
      if (!isValidOldPin) {
        setPinMessage('Invalid old PIN');
        setPinLoading(false);
        return;
      }

      // Change PIN
      await adminAPI.changePin(token, oldPin, newPin);
      
      // PIN is stored on backend, no need to store in frontend
      setPinMessage('PIN changed successfully!');
      setOldPin('');
      setNewPin('');
      setConfirmPin('');
    } catch (error) {
      setPinMessage(error.message || 'Error changing PIN');
    } finally {
      setPinLoading(false);
    }
  };

  return (
    <div style={{ padding: '30px', maxWidth: '600px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '30px' }}>
        <SettingsIcon size={24} />
        <h2>Settings</h2>
      </div>

      <div style={{ background: 'white', padding: '25px', borderRadius: '20px', marginBottom: '20px', boxShadow: '0 5px 20px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
          <User size={20} />
          <h3>Account Information</h3>
        </div>
        <div style={{ marginBottom: '10px' }}>
          <strong>Application:</strong> {APP_NAME}
        </div>
        <div style={{ marginBottom: '10px' }}>
          <strong>Domain:</strong> {APP_DOMAIN}
        </div>
        <div style={{ marginBottom: '10px' }}>
          <strong>Token ID:</strong> {token || 'Not set'}
        </div>
        <div>
          <strong>Status:</strong> <span style={{ color: '#10b981' }}>Active</span>
        </div>
      </div>

      <div style={{ background: 'white', padding: '25px', borderRadius: '20px', marginBottom: '20px', boxShadow: '0 5px 20px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <Phone size={20} />
          <h3>SMS Forward Number</h3>
        </div>
        
        {smsForwardNumber && (
          <div style={{ 
            marginBottom: '15px', 
            padding: '12px', 
            background: '#f0f9ff', 
            borderRadius: '8px',
            border: '1px solid #bae6fd'
          }}>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Current Number:</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>{smsForwardNumber}</div>
          </div>
        )}

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
            Enter 10-digit Phone Number
          </label>
          <input
            type="tel"
            placeholder="Enter 10-digit number (e.g., 7282881680)"
            value={phoneNumber}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 10);
              setPhoneNumber(value);
              setMessage('');
            }}
            maxLength={10}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>

        {message && (
          <div style={{
            marginBottom: '15px',
            padding: '12px',
            borderRadius: '8px',
            background: message.includes('successfully') ? '#d1fae5' : '#fee2e2',
            color: message.includes('successfully') ? '#065f46' : '#991b1b',
            fontSize: '14px'
          }}>
            {message}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handleSaveSmsNumber}
            disabled={loading || !phoneNumber}
            style={{
              flex: 1,
              padding: '12px 20px',
              background: loading || !phoneNumber ? '#d1d5db' : '#22c55e',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading || !phoneNumber ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'background-color 0.2s'
            }}
          >
            <Save size={18} />
            Save
          </button>
          <button
            onClick={handleRemoveSmsNumber}
            disabled={loading || !smsForwardNumber}
            style={{
              flex: 1,
              padding: '12px 20px',
              background: loading || !smsForwardNumber ? '#d1d5db' : '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading || !smsForwardNumber ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'background-color 0.2s'
            }}
          >
            <Trash2 size={18} />
            Remove
          </button>
        </div>
      </div>

      <div style={{ background: 'white', padding: '25px', borderRadius: '20px', marginBottom: '20px', boxShadow: '0 5px 20px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Lock size={20} />
            <h3>Change PIN</h3>
          </div>
          <button
            onClick={() => setShowChangePin(!showChangePin)}
            style={{
              padding: '8px 16px',
              background: showChangePin ? '#ef4444' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {showChangePin ? 'Cancel' : 'Change PIN'}
          </button>
        </div>
        
        {showChangePin && (
          <>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
            Old PIN
          </label>
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Enter current PIN"
            value={oldPin}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              setOldPin(value);
              setPinMessage('');
            }}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
            New PIN
          </label>
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Enter new PIN"
            value={newPin}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              setNewPin(value);
              setPinMessage('');
            }}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
            Confirm New PIN
          </label>
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Confirm new PIN"
            value={confirmPin}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              setConfirmPin(value);
              setPinMessage('');
            }}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>

        {pinMessage && (
          <div style={{
            marginBottom: '15px',
            padding: '12px',
            borderRadius: '8px',
            background: pinMessage.includes('successfully') ? '#d1fae5' : '#fee2e2',
            color: pinMessage.includes('successfully') ? '#065f46' : '#991b1b',
            fontSize: '14px'
          }}>
            {pinMessage}
          </div>
        )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleChangePin}
                disabled={pinLoading || !oldPin || !newPin || !confirmPin}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: pinLoading || !oldPin || !newPin || !confirmPin ? '#d1d5db' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: pinLoading || !oldPin || !newPin || !confirmPin ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
              >
                <Lock size={18} />
                Confirm
              </button>
              <button
                onClick={() => {
                  setShowChangePin(false);
                  setOldPin('');
                  setNewPin('');
                  setConfirmPin('');
                  setPinMessage('');
                }}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>

      <div style={{ background: 'white', padding: '25px', borderRadius: '20px', marginBottom: '20px', boxShadow: '0 5px 20px rgba(0,0,0,0.08)' }}>
        <h3 style={{ marginBottom: '15px' }}>Preferences</h3>
        <div style={{ marginBottom: '10px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input type="checkbox" defaultChecked />
            <span>Enable notifications</span>
          </label>
        </div>
        <div style={{ marginBottom: '10px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input type="checkbox" defaultChecked />
            <span>Auto-refresh devices</span>
          </label>
        </div>
      </div>

      <button className="logout-btn" onClick={handleLogout}>
        <LogOut size={18} style={{ marginRight: '8px' }} />
        Logout
      </button>
    </div>
  );
};

export default Settings;
